import { defineStore } from 'pinia'
import { ref, shallowReactive, watch } from 'vue'
import type { Ref } from 'vue'
import { debounce } from '@/utils/lodash'

export type ControlOption = {
  value: string
  label: string
  color?: string
  disabled?: boolean
}

export type ControlSchema = {
  min?: number | { x: number; y: number; z: number }
  max?: number | { x: number; y: number; z: number }
  step?: number | { x: number; y: number; z: number }
  boolean?: boolean
  checkbox?: boolean
  color?: boolean
  bezier?: boolean
  /** File input — value is the `accept` attribute (e.g. `'image/*'`). Config stores the object URL. */
  file?: string
  label?: string
  options?: string[] | ControlOption[]
  component?: string
  direction?: 'row' | 'column'
  callback?: string
  sectionStart?: boolean
  /** When set, syncs this config value to a URL query parameter with the given name. */
  queryParam?: string
}

export type ConfigControlsSchema = {
  [key: string]: ControlSchema | ConfigControlsSchema | string[]
}

type OnChangeCallback = () => void

type ConfigEntry = {
  config: Ref<Record<string, unknown>>
  schema: ConfigControlsSchema
  onChange?: OnChangeCallback
  stopWatch?: () => void
  callbacks?: Record<string, () => void>
}

const DEFAULT_DEBOUNCE_DELAY = 500

export const useViewConfigStore = defineStore('viewConfig', () => {
  // shallowReactive prevents Vue from unwrapping nested refs inside entries
  const registry = shallowReactive<Record<string, ConfigEntry>>({})
  const version = ref(0)

  const registerViewConfig = (
    routeName: string,
    config: Ref<Record<string, unknown>>,
    schema: ConfigControlsSchema,
    onChange?: OnChangeCallback,
    callbacks?: Record<string, () => void>
  ) => {
    const existingEntry = registry[routeName]
    if (existingEntry?.stopWatch) {
      existingEntry.stopWatch()
    }

    let stopWatch: (() => void) | undefined

    if (onChange) {
      const debouncedCallback = debounce(onChange, DEFAULT_DEBOUNCE_DELAY)
      stopWatch = watch(
        () => JSON.stringify(config.value),
        () => {
          debouncedCallback()
        }
      )
    }

    // eslint-disable-next-line functional/immutable-data
    registry[routeName] = { config, schema, onChange, stopWatch, callbacks }
    version.value++
  }

  const unregisterViewConfig = (routeName: string) => {
    const entry = registry[routeName]
    if (entry?.stopWatch) {
      entry.stopWatch()
    }
    // eslint-disable-next-line functional/immutable-data
    delete registry[routeName]
    version.value++
  }

  const updateViewSchema = (routeName: string, schema: ConfigControlsSchema) => {
    const entry = registry[routeName]
    if (!entry) return
    // A fresh entry object, not a mutation of the existing one: registry is shallowReactive,
    // so only a reassignment of registry[routeName] itself is tracked. Mutating entry.schema in
    // place leaves the entry reference unchanged, which makes ConfigPanel's currentEntry
    // computed (itself returning that same reference) look unchanged to Vue and silently skip
    // notifying currentSchema, even though version bumped.
    // eslint-disable-next-line functional/immutable-data
    registry[routeName] = { ...entry, schema }
    version.value++
  }

  const getConfigValue = (routeName: string, path: string): unknown => {
    const config = registry[routeName]?.config.value
    if (!config) return undefined
    return path
      .split('.')
      .reduce<unknown>((object, key) => (object as Record<string, unknown>)?.[key], config)
  }

  const updateConfig = (routeName: string, path: string, value: unknown) => {
    const entry = registry[routeName]
    if (!entry) return

    const keys = path.split('.')
    let object = entry.config.value

    keys.slice(0, -1).forEach((key) => {
      // eslint-disable-next-line functional/immutable-data
      if (object[key] === undefined) object[key] = {}
      object = object[key] as Record<string, unknown>
    })

    // eslint-disable-next-line functional/immutable-data
    object[keys[keys.length - 1]] = value
  }

  const invokeCallback = (routeName: string, name: string) => {
    registry[routeName]?.callbacks?.[name]?.()
  }

  const resetState = () => {
    Object.keys(registry).forEach((key) => {
      const entry = registry[key]
      if (entry?.stopWatch) entry.stopWatch()
      // eslint-disable-next-line functional/immutable-data
      delete registry[key]
    })
    version.value = 0
  }

  return {
    registry,
    version,
    registerViewConfig,
    unregisterViewConfig,
    updateViewSchema,
    getConfigValue,
    updateConfig,
    invokeCallback,
    resetState
  }
})

/**
 * Copies a view's config into a ref, so the panel can edit it without touching the constant.
 * @param config The view's default config.
 * @returns A deep copy, wrapped in a ref.
 */
export const createReactiveConfig = <T extends Record<string, unknown>>(config: T): Ref<T> =>
  ref(JSON.parse(JSON.stringify(config))) as Ref<T>

// Standalone convenience exports — same call site ergonomics as before
/**
 * Gives the Config panel to a view: its config, the schema the panel builds controls from, and
 * callbacks for changes and buttons.
 * @param routeName The view's route name.
 * @param config The config the panel edits.
 * @param schema The controls to show.
 * @param onChange Called with each change.
 * @param callbacks Handlers for the schema's buttons, by name.
 */
export const registerViewConfig = (
  routeName: string,
  config: Ref<Record<string, unknown>>,
  schema: ConfigControlsSchema,
  onChange?: OnChangeCallback,
  callbacks?: Record<string, () => void>
): void => {
  useViewConfigStore().registerViewConfig(routeName, config, schema, onChange, callbacks)
}

/**
 * Removes a view's Config panel registration.
 * @param routeName The view's route name.
 */
export const unregisterViewConfig = (routeName: string): void => {
  useViewConfigStore().unregisterViewConfig(routeName)
}

/**
 * Replaces the controls a registered view's Config panel shows.
 * @param routeName The view's route name.
 * @param schema The new controls.
 */
export const updateViewSchema = (routeName: string, schema: ConfigControlsSchema): void => {
  useViewConfigStore().updateViewSchema(routeName, schema)
}
